import { CriterionScale } from '../../../tools/CriteriaScales';
import type { CriterionValueDisplay } from '../../../tools/useItemCriteriaValues';
import { ArrowLeft } from 'lucide-react';

export interface PairwiseLiveItem {
  id: string;
  name: string;
  criteriaValues?: CriterionValueDisplay[];
}

interface PairwiseLiveProps {
  item1: PairwiseLiveItem;
  item2: PairwiseLiveItem;
  onSelect: (winnerId: string) => void;
  onUndo?: () => void;
  canUndo?: boolean;
  current: number;
  total: number;
}

function ItemCard({
  item,
  label,
  hoverClass,
  onClick,
}: {
  item: PairwiseLiveItem;
  label: string;
  hoverClass: string;
  onClick: () => void;
}) {
  const hasCriteria = (item.criteriaValues?.length ?? 0) > 0;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left  border border-[var(--rule-bright)] bg-[var(--surface)] p-6 transition-colors ${hoverClass}`}
    >
      <span className="font-heading text-[0.5rem] text-[var(--text-dim)]">{label}</span>
      <div className="font-display text-[1.05rem] text-[var(--text-pri)] mt-2 leading-snug">
        {item.name}
      </div>
      {hasCriteria && (
        <div className="mt-4 pt-3 border-t border-[var(--surface-raised)] space-y-2">
          {item.criteriaValues!.map((cv) => (
            <CriterionScale key={cv.id} {...cv} variant="live" />
          ))}
        </div>
      )}
    </button>
  );
}

export function PairwiseLive({
  item1,
  item2,
  onSelect,
  onUndo,
  canUndo,
  current,
  total,
}: PairwiseLiveProps) {
  const pct = total > 0 ? Math.round((current / total) * 100) : 0;

  return (
    <div>
      <div className="flex justify-between font-heading text-[0.55rem] tracking-[0.07em] text-[var(--text-ter)] mb-2">
        <span>
          Comparison <span className="text-[var(--text-pri)]">{current}</span> of {total}
        </span>
        <span>{pct}%</span>
      </div>
      <div className="h-0.5 bg-[var(--rule-bright)] rounded-full overflow-hidden mb-6">
        <div className="h-full bg-alpine-green-500/75 transition-all" style={{ width: `${pct}%` }} />
      </div>

      <p className="font-body text-[0.9rem] text-center text-[var(--text-sec)] mb-6">
        Which is more important <em className="text-[var(--text-pri)]">right now</em>?
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <ItemCard
          item={item1}
          label="Option 1"
          hoverClass="hover:border-luminous-coral-700/40 hover:bg-luminous-coral-700/5"
          onClick={() => onSelect(item1.id)}
        />
        <ItemCard
          item={item2}
          label="Option 2"
          hoverClass="hover:border-electric-mustard-600/40 hover:bg-electric-mustard-600/5"
          onClick={() => onSelect(item2.id)}
        />
      </div>

      {canUndo && onUndo && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={onUndo}
            className="inline-flex items-center gap-1.5 font-heading text-[0.55rem] tracking-[0.08em] uppercase text-[var(--text-ter)] hover:text-[var(--text-sec)]"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Undo last pick
          </button>
        </div>
      )}
    </div>
  );
}
